import * as React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Check } from 'lucide-react';

type PaymentExtras = {
    phone: string;
    email: string;
};

type PaymentDialogProps = {
    buttonId: string;
    triggerText: string;
    amount: number;
    onPay: (amount: number, title: string, extras: PaymentExtras) => Promise<void> | void;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

export function PaymentDialog({
    buttonId,
    triggerText,
    amount,
    onPay,
    open: openProp,
    onOpenChange,
}: PaymentDialogProps) {
    const [openUncontrolled, setOpenUncontrolled] = React.useState(false);
    const open = openProp ?? openUncontrolled;
    const setOpen = onOpenChange ?? setOpenUncontrolled;

    const [form, setForm] = React.useState<PaymentExtras>({
        phone: '',
        email: '',
    });

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const isFormValid =
        form.phone.trim().length == 11 &&
        form.email.trim().length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email); // basic email validation

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!isFormValid) return;
        setError(null);
        setLoading(true);
        try {
            await onPay(amount, triggerText, form);
            setOpen(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'خطا در پرداخت');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    id={buttonId}
                    type='button'
                    className='bg-[#EEAC43] p-2 rounded-2xl text-black hover:brightness-110 text-center font-bold text-xl'
                >
                    {triggerText}
                </button>
            </DialogTrigger>

            <DialogContent className='bg-white max-lg:w-11/12 rounded-lg dark:bg-primary-active dark:border-white/10'>
                <DialogTitle className='text-xl font-bold'>{triggerText}</DialogTitle>

                <form className='flex flex-col gap-y-2 mt-6'>
                    <div className={'relative w-full overflow-hidden'}>
                        <input
                            dir={'ltr'}
                            className='w-full border border-gray-100 bg-gray-50 dark:bg-white/5 dark:border-white/10 p-2 rounded-lg'
                            id='phone'
                            type='tel'
                            placeholder='شماره تماس'
                            value={form.phone}
                            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        />
                        <Check
                            size={12}
                            className={`${form.phone.trim().length == 11 ? '' : 'translate-x-9'} transition-all absolute top-1/2 -translate-y-1/2 start-3 bg-green-500 p-1 rounded-full box-content`}
                        />
                    </div>
                    <div className={'relative w-full overflow-hidden'}>
                        <input
                            dir={'ltr'}
                            className='w-full border border-gray-100 bg-gray-50 dark:bg-white/5 dark:border-white/10 p-2 rounded-lg'
                            id='email'
                            type='email'
                            placeholder='ایمیل'
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        />
                        <Check
                            size={12}
                            className={`${form.email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? '' : 'translate-x-9'} transition-all absolute top-1/2 -translate-y-1/2 start-3 bg-green-500 p-1 rounded-full box-content`}
                        />
                    </div>

                    {error && <div className='text-red-600 text-sm mt-1'>{error}</div>}

                    <button
                        className='bg-[#EEAC43] p-2 rounded-lg text-black hover:brightness-110 text-center font-bold text-xl disabled:opacity-60'
                        id={`${buttonId}-submit`}
                        type='button'
                        onClick={handleClick}
                        disabled={loading || !isFormValid} // 🔹 disabled when form invalid
                    >
                        {loading ? 'در حال انتقال...' : triggerText}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
